import axios, { AxiosInstance, AxiosResponse } from 'axios';

// API base URL - change this to match your backend configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || '/';

// Create axios instance
const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Important for session cookies
});

// Types for API responses
export interface User {
  id: number;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  is_active: boolean;
  is_verified: boolean;
  email_verified_at?: string;
  created_at: string;
  updated_at: string;
}

export interface LoginRequest {
  username: string;
  password: string;
}

export interface LoginResponse {
  token: string;
  user: User;
}

export interface RegisterRequest {
  username: string;
  email: string;
  password: string;
  first_name?: string;
  last_name?: string;
}

export interface ApiResponse<T = any> {
  code: number;
  message: string;
  data: T;
}

export interface Application {
  id: number;
  name: string;
  description: string;
  app_id: string;
  app_token?: string; // Only included on creation or token regeneration
  is_active: boolean;
  scopes: string;
  created_at: string;
  updated_at: string;
}

export interface CreateApplicationRequest {
  name: string;
  description?: string;
  scopes?: string;
}

export interface UpdateApplicationRequest {
  name?: string;
  description?: string;
  scopes?: string;
  is_active?: boolean;
}

export interface MockPomodoroRequest {
  application_id: number;
  description: string;
}

export interface Pomodoro {
  id: number;
  start_time: string;
  end_time: string;
  description: string;
  source: string;
  ip_address: string;
  client: string;
  created_at: string;
  updated_at: string;
}

export interface PomodoroListQuery {
  page?: number;
  page_size?: number;
  keyword?: string;
  date_from?: string;
  date_to?: string;
}

export interface PomodoroPaginatedResponse {
  items: Pomodoro[];
  page: number;
  page_size: number;
  total: number;
  total_pages: number;
}

// Auth API functions
export const authAPI = {
  login: async (credentials: LoginRequest): Promise<LoginResponse> => {
    const response: AxiosResponse<ApiResponse<LoginResponse>> = await api.post('/web/v1/auth/login', credentials);
    return response.data.data;
  },

  register: async (userData: RegisterRequest): Promise<User> => {
    const response: AxiosResponse<ApiResponse<{ user: User }>> = await api.post('/web/v1/auth/register', userData);
    return response.data.data.user;
  },

  logout: async (): Promise<void> => {
    await api.post('/web/v1/users/logout');
  },

  getProfile: async (): Promise<User> => {
    const response: AxiosResponse<ApiResponse<User>> = await api.get('/web/v1/users/profile');
    return response.data.data;
  },

  verifyEmail: async (token: string): Promise<void> => {
    await api.post('/web/v1/auth/verify-email', { token });
  },

  resendVerification: async (email: string): Promise<void> => {
    await api.post('/web/v1/auth/resend-verification', { email });
  },
};

// Application API functions
export const appAPI = {
  // Get all applications for the authenticated user
  getApplications: async (): Promise<Application[]> => {
    const response: AxiosResponse<ApiResponse<Application[]>> = await api.get('/web/v1/applications');
    return response.data.data;
  },

  // Get a specific application by ID
  getApplication: async (id: number): Promise<Application> => {
    const response: AxiosResponse<ApiResponse<Application>> = await api.get(`/web/v1/applications/${id}`);
    return response.data.data;
  },

  // Create a new application
  createApplication: async (data: CreateApplicationRequest): Promise<Application> => {
    const response: AxiosResponse<ApiResponse<Application>> = await api.post('/web/v1/applications', data);
    return response.data.data;
  },

  // Update an existing application
  updateApplication: async (id: number, data: UpdateApplicationRequest): Promise<Application> => {
    const response: AxiosResponse<ApiResponse<Application>> = await api.put(`/web/v1/applications/${id}`, data);
    return response.data.data;
  },

  // Delete an application
  deleteApplication: async (id: number): Promise<void> => {
    await api.delete(`/web/v1/applications/${id}`);
  },

  // Regenerate application token
  regenerateToken: async (id: number): Promise<Application> => {
    const response: AxiosResponse<ApiResponse<Application>> = await api.post(`/web/v1/applications/${id}/regenerate-token`);
    return response.data.data;
  },
};

// Pomodoro API functions
export const pomodoroAPI = {
  // Get paginated list of pomodoros
  getPomodoros: async (query?: PomodoroListQuery): Promise<PomodoroPaginatedResponse> => {
    const params = new URLSearchParams();
    if (query?.page) params.append('page', query.page.toString());
    if (query?.page_size) params.append('page_size', query.page_size.toString());
    if (query?.keyword) params.append('keyword', query.keyword);
    if (query?.date_from) params.append('date_from', query.date_from);
    if (query?.date_to) params.append('date_to', query.date_to);

    const url = `/web/v1/pomodoros${params.toString() ? `?${params.toString()}` : ''}`;
    const response: AxiosResponse<ApiResponse<PomodoroPaginatedResponse>> = await api.get(url);
    return response.data.data;
  },

  // Get paginated list of pomodoros for a specific application
  getPomodorosByApp: async (appId: number, query?: PomodoroListQuery): Promise<PomodoroPaginatedResponse> => {
    const params = new URLSearchParams();
    if (query?.page) params.append('page', query.page.toString());
    if (query?.page_size) params.append('page_size', query.page_size.toString());
    if (query?.keyword) params.append('keyword', query.keyword);
    if (query?.date_from) params.append('date_from', query.date_from);
    if (query?.date_to) params.append('date_to', query.date_to);

    const url = `/web/v1/pomodoros${params.toString() ? `?${params.toString()}` : ''}`;
    const response: AxiosResponse<ApiResponse<PomodoroPaginatedResponse>> = await api.get(url);
    return response.data.data;
  },

  // Create a mock pomodoro (25 minutes ago to now)
  mockPomodoro: async (data: MockPomodoroRequest): Promise<Pomodoro> => {
    const response: AxiosResponse<ApiResponse<Pomodoro>> = await api.post('/web/v1/pomodoros/mock', data);
    return response.data.data;
  },
};

// Request interceptor to add auth token if available
api.interceptors.request.use(
  (config) => {
    // Token is handled via cookies for session-based auth
    // If using token-based auth, you would add it here:
    // const token = localStorage.getItem('token');
    // if (token) {
    //   config.headers.Authorization = `Bearer ${token}`;
    // }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Global error handler function
let globalErrorHandler: ((message: string) => void) | null = null;

export const setGlobalErrorHandler = (handler: (message: string) => void) => {
  globalErrorHandler = handler;
};

// Response interceptor to handle common errors
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Only redirect to login if we're not already on the login page
    if (error.response?.status === 401 && !window.location.pathname.includes('/login')) {
      // Unauthorized - redirect to login
      window.location.href = '/login';
    }

    // Show error message using global error handler if available
    // Don't show errors on login page (it has its own error handling)
    // Don't show network errors on login page
    const isLoginPage = window.location.pathname.includes('/login');
    const isNetworkError = !error.response;
    const errorMessage = error.response?.data?.message ||
                        error.message ||
                        'An unexpected error occurred';

    if (globalErrorHandler && !isLoginPage && (!isNetworkError || error.config?.url !== '/web/v1/auth/login')) {
      globalErrorHandler(errorMessage);
    }

    return Promise.reject(error);
  }
);

export default api;